import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, BookOpen, Scale, TrendingUp, Calculator,
  Network, Users, Truck, FileText, ArrowDownLeft, ArrowUpRight,
  Landmark, CreditCard, Building2, GitBranch, UserCheck,
  Shield, Settings, BarChart3, Package, ShoppingCart,
  ShoppingBag, Boxes, Users2, ChevronDown, ChevronUp,
  BookMarked, PieChart, CalendarRange, X,
} from 'lucide-react';
import clsx from 'clsx';
import useAuthStore from '../../store/authStore';
import useLang from '../../i18n';
import useCompany from '../../hooks/useCompany';

const getMenu = (t) => [
  { key:'dashboard', label:t('dashboard'), icon:LayoutDashboard, to:'/dashboard', single:true },
  {
    key:'sales', label:t('sales'), icon:ShoppingCart,
    children:[
      { label:t('salesList'),   to:'/sales',     icon:ShoppingCart },
      { label:t('invoices'), to:'/invoices',  icon:FileText },
      { label:t('customers'),  to:'/customers', icon:Users },
    ],
  },
  {
    key:'purchases', label:t('purchases'), icon:ShoppingBag,
    children:[
      { label:t('purchases'),   to:'/purchases', icon:ShoppingBag },
    ],
  },
  {
    key:'inventory', label:t('inventory'), icon:Boxes,
    children:[
      { label:t('products'),       to:'/products',  icon:Package },
      { label:t('inventoryReport'), to:'/inventory', icon:Boxes },
    ],
  },
  { key:'accounting', label:t('accounting'), icon:BookOpen, to:'/accounting', single:true },
  {
    key:'finance', label:t('finance'), icon:CreditCard,
    children:[
      { label:t('cashbox'),   to:'/cashbox',  icon:CreditCard },
      { label:t('expenses'),   to:'/expenses', icon:ArrowUpRight },
      { label:t('revenues'), to:'/revenues', icon:ArrowDownLeft },
      { label:t('partners'),    to:'/partners', icon:Users2 },
    ],
  },
  {
    key:'admin', label:t('management'), icon:Settings,
    children:[
      { label:t('branches'),     to:'/branches',     icon:GitBranch },
      { label:t('users'),   to:'/users',        icon:UserCheck },
      { label:t('budget'),     to:'/budget',       icon:PieChart },
      { label:t('assets'),to:'/assets',     icon:Building2 },
      { label:t('fiscalYears'),  to:'/fiscal-years', icon:CalendarRange },
      { label:t('audit'),     to:'/audit',        icon:Shield },
      { label:t('settings'),   to:'/settings',     icon:Settings },
    ],
  },
  { key:'reports', label:t('reports'), icon:BarChart3, to:'/reports', single:true },
];

export default function Sidebar({ onClose }) {
  const { user } = useAuthStore();
  const { t, lang } = useLang();
  const { company } = useCompany();
  const MENU = getMenu(t);
  const location = useLocation();
  const [open, setOpen] = useState(() => {
    const found = MENU.find(m => m.children?.some(c => location.pathname.startsWith(c.to)));
    return found ? { [found.key]: true } : {};
  });

  const toggle = (key) => setOpen(prev => ({ ...prev, [key]: !prev[key] }));
  const isChildActive = (children) => children?.some(c => location.pathname.startsWith(c.to));

  return (
    <aside className="w-64 bg-white border-l border-gray-200 h-screen flex flex-col shadow-sm">
      {/* Logo */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          {company.logo ? (
            <img src={company.logo} alt="logo" className="w-9 h-9 rounded-xl object-contain flex-shrink-0 border border-gray-100" />
          ) : (
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
          )}
          <div className="overflow-hidden">
            <div className="text-gray-900 text-sm font-bold leading-tight truncate">
              {company.name || (lang==="en" ? "Accounting System" : t('companyName'))}
            </div>
            <div className="text-gray-400 text-[10px]">{'v1.0'}</div>
          </div>
        </div>
        {/* Close button for mobile */}
        {onClose && (
          <button onClick={onClose} className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-2 px-2" style={{ scrollbarWidth:'thin' }}>
        {MENU.map(item => {
          if (item.single) {
            return (
              <NavLink key={item.key} to={item.to} onClick={onClose}
                className={({ isActive }) => clsx(
                  'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all mb-0.5',
                  isActive ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
                )}>
                <item.icon className="w-4 h-4 flex-shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
          }

          const active = isChildActive(item.children);
          const isOpen = open[item.key] || active;

          return (
            <div key={item.key} className="mb-0.5">
              <button onClick={() => toggle(item.key)}
                className={clsx(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all',
                  active ? 'text-blue-700 bg-blue-50' : 'text-gray-600 hover:bg-gray-100'
                )}>
                <item.icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 text-right">{item.label}</span>
                {isOpen ? <ChevronUp className="w-3.5 h-3.5 flex-shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />}
              </button>
              {isOpen && (
                <div className="mr-4 mt-0.5 border-r-2 border-gray-100 pr-2 space-y-0.5 mb-1">
                  {item.children.map(child => (
                    <NavLink key={child.to} to={child.to} onClick={onClose}
                      className={({ isActive }) => clsx(
                        'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                        isActive ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:bg-gray-100'
                      )}>
                      <child.icon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{child.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <div className="h-4" />
      </nav>

      {/* User */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold flex-shrink-0">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-gray-800 text-xs font-semibold truncate">{user?.name}</p>
            <p className="text-gray-400 text-[10px] truncate">{user?.email}</p>
          </div>
        </div>
      </div>
    </aside>
  );
}
